import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 pt-24 pb-16">
        <Card className="max-w-4xl mx-auto bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-4xl font-bold text-white mb-2">
              Privacy Policy
            </CardTitle>
            <p className="text-slate-400">Last updated: January 2025</p>
          </CardHeader>

          <CardContent className="prose prose-invert max-w-none space-y-8 text-slate-300">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                Welcome to BirdenMedia ("we," "our," or "us"). We are committed to protecting your personal 
                information and your right to privacy. This Privacy Policy explains how we collect, use, 
                disclose, and safeguard your information when you visit our website 
                <span className="text-blue-400"> birdenmedia.co</span> and use our services.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this 
                privacy policy, please do not access the site.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">2.1 Personal Information</h3>
              <p className="mb-4">
                When you submit an inquiry or contact form on our website, we collect the following 
                personal information:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>First and Last Name</li>
                <li>Company Name</li>
                <li>Email Address</li>
                <li>Phone Number</li>
                <li>Service Type of Interest</li>
                <li>Budget Range</li>
                <li>Project Timeline</li>
                <li>Message/Project Description</li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">2.2 Automatically Collected Information</h3>
              <p className="mb-4">
                When you visit our website, we automatically collect certain information about your 
                device and browsing behavior:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>IP Address</li>
                <li>Browser Type and Version</li>
                <li>Device Information</li>
                <li>Referral Source (where you came from)</li>
                <li>Pages Visited and Time Spent on Pages</li>
                <li>UTM Parameters (marketing campaign tracking)</li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* How We Use Your Information */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
              <p className="mb-4">We use the information we collect for the following purposes:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">To Provide Services:</strong> Process your inquiries and provide you with the services you request</li>
                <li><strong className="text-white">To Communicate:</strong> Send you information about our services, respond to your questions, and provide customer support</li>
                <li><strong className="text-white">To Improve:</strong> Analyze website usage to improve our services and user experience</li>
                <li><strong className="text-white">Marketing:</strong> Send promotional materials about our services (you can opt-out at any time)</li>
                <li><strong className="text-white">Analytics:</strong> Track website performance and marketing campaign effectiveness</li>
                <li><strong className="text-white">Legal Compliance:</strong> Comply with applicable laws and regulations</li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Cookies and Tracking */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="mb-4">
                We use cookies and similar tracking technologies to track activity on our website and 
                store certain information. Technologies used include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Google Analytics 4:</strong> To analyze website traffic and user behavior</li>
                <li><strong className="text-white">Meta Pixel:</strong> To track conversions and optimize advertising campaigns</li>
                <li><strong className="text-white">Session Cookies:</strong> To maintain your session and provide website functionality</li>
              </ul>
              <p className="mt-4">
                You can instruct your browser to refuse all cookies or to indicate when a cookie is being 
                sent. However, if you do not accept cookies, you may not be able to use some portions of 
                our website.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. How We Share Your Information</h2>
              <p className="mb-4">
                We do not sell, trade, or rent your personal information to third parties. We may share 
                your information in the following circumstances:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Service Providers:</strong> Third-party vendors who perform services on our behalf (e.g., email services, analytics providers)</li>
                <li><strong className="text-white">Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, sale, or acquisition of our business</li>
                <li><strong className="text-white">With Your Consent:</strong> When you explicitly agree to share your information</li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information against unauthorized access, alteration, disclosure, or destruction. 
                These measures include:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Encrypted data transmission (HTTPS/SSL)</li>
                <li>Secure server infrastructure</li>
                <li>Rate limiting and anti-bot protection</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
              </ul>
              <p className="mt-4">
                However, no method of transmission over the Internet or electronic storage is 100% secure, 
                and we cannot guarantee absolute security.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Data Retention</h2>
              <p>
                We retain your personal information only for as long as necessary to fulfill the purposes 
                outlined in this privacy policy, unless a longer retention period is required or permitted 
                by law. Lead information is typically retained for:
              </p>
              <ul className="list-disc pl-6 space-y-2 mt-4">
                <li>Active leads: Until the project is completed or declined</li>
                <li>Inactive leads: Up to 2 years for potential future opportunities</li>
                <li>Analytics data: Aggregated and anonymized indefinitely</li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Your Privacy Rights</h2>
              <p className="mb-4">
                Depending on your location, you may have the following rights regarding your personal 
                information:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Access:</strong> Request a copy of the personal information we hold about you</li>
                <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information</li>
                <li><strong className="text-white">Deletion:</strong> Request deletion of your personal information</li>
                <li><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications</li>
                <li><strong className="text-white">Data Portability:</strong> Request transfer of your data to another service</li>
                <li><strong className="text-white">Object:</strong> Object to processing of your personal information</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us at{' '}
                <a href="mailto:legal@birdenmedia.co" className="text-blue-400 hover:text-blue-300 underline">
                  legal@birdenmedia.co
                </a>
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Links</h2>
              <p>
                Our website may contain links to third-party websites. We are not responsible for the 
                privacy practices of these external sites. We encourage you to review the privacy policies 
                of any third-party sites you visit.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
              <p>
                Our services are not directed to individuals under the age of 18. We do not knowingly 
                collect personal information from children. If you are a parent or guardian and believe 
                your child has provided us with personal information, please contact us.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to This Privacy Policy</h2>
              <p>
                We may update this privacy policy from time to time. We will notify you of any changes by 
                posting the new privacy policy on this page and updating the "Last updated" date. You are 
                advised to review this privacy policy periodically for any changes.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-slate-900/50 p-6 rounded-lg border border-slate-700/50">
                <p className="mb-2"><strong className="text-white">Email:</strong> legal@birdenmedia.co</p>
                <p><strong className="text-white">Website:</strong> birdenmedia.co</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-slate-800/50 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © 2025 BirdenMedia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
